import type { KeyValuePair } from "./normalizeSectionInputs";

export interface CheckboxMeta {
  sequence?: string;
  isSignature: boolean;
  label?: string;
}

export interface ParsedFormMeta {
  checkboxIndex: Map<string, CheckboxMeta[]>;
  signatureKeyToLabel: Map<string, string>;
}

function normalizeText(txt: string): string {
  return txt.replace(/\u00A0|&nbsp;/g, " ").replace(/\s+/g, " ").trim();
}

function extractLabelForField(container: HTMLElement, fieldId: string): string {
  if (!fieldId) return "";
  const forLabel = container.querySelector(`label[for="${CSS.escape(fieldId)}"]`) as HTMLLabelElement | null;
  if (forLabel && forLabel.textContent) return normalizeText(forLabel.textContent);
  const inputEl = (container.querySelector(`#${CSS.escape(fieldId)}`)
    || container.querySelector(`[data-id="${CSS.escape(fieldId)}"]`)
    || container.querySelector(`[data-field-id="${CSS.escape(fieldId)}"]`)) as HTMLElement | null;
  if (inputEl) {
    const parentLabel = inputEl.closest('label');
    if (parentLabel && parentLabel.textContent) return normalizeText(parentLabel.textContent);
    const prev = inputEl.previousElementSibling as HTMLElement | null;
    if (prev && prev.textContent) return normalizeText(prev.textContent);
    const aria = inputEl.getAttribute('aria-label');
    if (aria) return normalizeText(aria);
  }
  const display = container.querySelector(`[data-signature-field="${CSS.escape(fieldId)}"]`) as HTMLElement | null;
  if (display && display.previousElementSibling && display.previousElementSibling.textContent) {
    return normalizeText(display.previousElementSibling.textContent);
  }
  return "";
}

function isSignatureElement(el: HTMLElement): boolean {
  const idAttr = (el.getAttribute('id') || '').toLowerCase();
  const nameAttr = (el.getAttribute('name') || '').toLowerCase();
  const dataIdAttr = (el.getAttribute('data-id') || '').toLowerCase();
  const dataType = (el.getAttribute('data-type') || '').toLowerCase();
  const dataFieldId = (el.getAttribute('data-field-id') || '').toLowerCase();
  return (
    idAttr.includes('signature') ||
    nameAttr.includes('signature') ||
    dataIdAttr.includes('signature') ||
    dataType === 'signature' ||
    dataFieldId.includes('signature')
  );
}

export function parseFormMetaFromHtml(html: string): ParsedFormMeta {
  const checkboxIndex = new Map<string, CheckboxMeta[]>();
  const signatureKeyToLabel = new Map<string, string>();
  if (!html || !html.trim()) return { checkboxIndex, signatureKeyToLabel };

  const temp = document.createElement('div');
  temp.innerHTML = html;

  // Collect all inputs to detect signature fields and checkbox sequences
  const allInputs = Array.from(temp.querySelectorAll<HTMLElement>('input, textarea, select'));

  // First pass: detect signatures and record labels by data-id key
  allInputs.forEach((el) => {
    const key = (el.getAttribute('data-id') || el.closest('[data-id]')?.getAttribute('data-id') || '').trim();
    if (!key) return;
    if (isSignatureElement(el)) {
      const labelKey = el.getAttribute('id') || el.getAttribute('data-field-id') || el.getAttribute('data-id') || '';
      const label = extractLabelForField(temp, labelKey);
      if (label && !signatureKeyToLabel.has(key)) signatureKeyToLabel.set(key, label);
    }
  });

  // Second pass: build checkboxIndex keyed by data-id with proper sequence
  const checkboxEls = allInputs.filter((el): el is HTMLInputElement => el instanceof HTMLInputElement && el.type === 'checkbox');
  // Group by key to allow index fallback to be per-key
  const grouped: Record<string, HTMLInputElement[]> = {};
  checkboxEls.forEach((cb) => {
    const key = (cb.getAttribute('data-id') || cb.closest('[data-id]')?.getAttribute('data-id') || '').trim();
    if (!key) return;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(cb);
  });

  Object.entries(grouped).forEach(([key, list]) => {
    const metas: CheckboxMeta[] = [];
    list.forEach((cb, idx) => {
      const sequenceRaw = (cb.getAttribute('data-order') || cb.closest('[data-order]')?.getAttribute('data-order') || '').trim();
      const sequence = sequenceRaw || undefined; // do not fallback to index; rely only on data-order
      const sig = isSignatureElement(cb);
      const labelKey = cb.getAttribute('id') || cb.getAttribute('data-field-id') || cb.getAttribute('data-id') || '';
      const label = sig ? (signatureKeyToLabel.get(key) || extractLabelForField(temp, labelKey)) : undefined;
      metas.push({ sequence, isSignature: sig, label });
    });
    checkboxIndex.set(key, metas);
  });

  return { checkboxIndex, signatureKeyToLabel };
}

export function buildPayloadFromMeta(
  meta: ParsedFormMeta,
  allPairs: KeyValuePair[],
  latestByKey: Map<string, string | number | boolean>,
  signatureOverrides?: Record<string, string>
) {
  const finalPayload: Array<{ key: string; value: string | number | boolean; signatureName?: string; sequence?: string }> = [];
  const valuesByKey = new Map<string, Array<string | number | boolean>>();
  allPairs.forEach((p) => {
    const arr = valuesByKey.get(p.key) || [];
    arr.push(p.value);
    valuesByKey.set(p.key, arr);
  });

  // Handle checkboxes first using meta
  meta.checkboxIndex.forEach((metaList, key) => {
    const vals = valuesByKey.get(key) || [];
    metaList.forEach((m, idx) => {
      const val = (vals[idx] ?? 'false') as string;
      const entry: { key: string; value: string; sequence?: string; signatureName?: string } = {
        key,
        value: val,
      };
      if (m.sequence) entry.sequence = m.sequence;
      const label = signatureOverrides?.[key] || m.label || meta.signatureKeyToLabel.get(key);
      if (m.isSignature && label) entry.signatureName = label;
      finalPayload.push(entry);
    });
  });

  // Handle remaining non-checkbox fields
  latestByKey.forEach((value, key) => {
    if (meta.checkboxIndex.has(key)) return; // already handled
    const label = signatureOverrides?.[key] || meta.signatureKeyToLabel.get(key);
    if (label) {
      finalPayload.push({ key, value, signatureName: label });
    } else {
      finalPayload.push({ key, value });
    }
  });

  return finalPayload;
}
